import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Divider,
  Alert,
} from '@mui/material';
import { ListingForm as ListingFormType } from '@/types';

interface BasicInfoTabProps {
  formData: ListingFormType;
  errors: Record<string, string>;
  onFormDataChange: (updates: Partial<ListingFormType>) => void;
  onErrorsChange: (errors: Record<string, string>) => void;
  clearErrors: (fields: string[]) => void;
}

const PROPERTY_TYPES = [
  { value: 'apartment', label: 'Apartment' },
  { value: 'house', label: 'House' },
  { value: 'condo', label: 'Condominium' },
  { value: 'villa', label: 'Villa' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'loft', label: 'Loft' },
  { value: 'cabin', label: 'Cabin' },
  { value: 'cottage', label: 'Cottage' },
  { value: 'studio', label: 'Studio' },
  { value: 'other', label: 'Other' },
];

const ROOM_TYPES = [
  { value: 'entire_place', label: 'Entire place' },
  { value: 'private_room', label: 'Private room' },
  { value: 'shared_room', label: 'Shared room' },
];

export const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  formData,
  errors,
  onFormDataChange,
  clearErrors,
}) => {
  const handleInputChange = (field: string, value: any) => {
    onFormDataChange({ [field]: value });
    clearErrors([field]);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Basic Information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Tell guests about your property with a compelling title and description.
      </Typography>

      <Grid container spacing={3}>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Property Title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            error={!!errors.title}
            helperText={errors.title || 'Create a catchy title that highlights your property\'s best features'}
            placeholder="Beautiful downtown apartment with city views"
            inputProps={{ maxLength: 100 }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description || 'Describe your property, its unique features, and what makes it special'}
            placeholder="Describe your property, its unique features, neighborhood highlights, and what makes it special for guests..."
            inputProps={{ maxLength: 2000 }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Property Type
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Help guests understand what type of property you're offering.
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors.propertyType}>
            <InputLabel>Property Type</InputLabel>
            <Select
              value={formData.propertyType || ''}
              onChange={(e) => handleInputChange('propertyType', e.target.value)}
              label="Property Type"
            >
              {PROPERTY_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
            {errors.propertyType && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {errors.propertyType}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors.roomType}>
            <InputLabel>Room Type</InputLabel>
            <Select
              value={formData.roomType || ''}
              onChange={(e) => handleInputChange('roomType', e.target.value)}
              label="Room Type"
            >
              {ROOM_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
            {errors.roomType && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {errors.roomType}
              </Typography>
            )}
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicInfoTab;